/**
 * <MarketNews> — 시장/종목 뉴스 Top 3
 *
 * 데이터: GET /api/ticker/news?scope=market 또는 ?scope=ticker&symbol=AAPL
 * 응답: { items: [{ title, source, url }] }
 *
 * options:
 *   scope: 'market' | 'ticker' (기본 market)
 *   symbol: 심볼 (scope=ticker일 때 필수)
 */
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import type { WidgetMeta, WidgetProps } from "../types";

interface NewsItem {
  title: string;
  titleKo?: string | null;  // 한국어 번역본 (있으면 우선 표시)
  description?: string;
  source: string;
  publisherLogoUrl?: string | null;
  url: string | null;
  imageUrl?: string | null;
  publishedAt?: string | null;
  tickers?: string[];
}

interface NewsResponse {
  scope: string;
  symbol: string | null;
  updatedAt: string;
  items: NewsItem[];
}

function timeAgoShort(iso?: string | null): string {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "";
  if (ms < 60_000) return "방금";
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}일 전`;
  return new Date(iso).toLocaleDateString("ko-KR", { month: "numeric", day: "numeric" });
}

// publishedAt → "YYYY-MM-DD" 키 (날짜별 그룹핑용 · 로컬 타임존)
function dateKey(iso?: string | null): string {
  if (!iso) return "unknown";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "unknown";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// "YYYY-MM-DD" → "오늘" / "어제" / "M/D (요일)"
function formatDateLabel(key: string): string {
  if (key === "unknown") return "최신";
  const [y, m, d] = key.split("-").map(Number);
  if (!y || !m || !d) return key;
  const date = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((today.getTime() - date.getTime()) / 86400000);
  if (diffDays === 0) return "오늘";
  if (diffDays === 1) return "어제";
  const dow = ["일", "월", "화", "수", "목", "금", "토"][date.getDay()];
  return `${m}/${d} (${dow})`;
}

const REFRESH_MS = 15 * 60 * 1000;

export function MarketNews({ options }: WidgetProps) {
  const scope =
    options?.scope === "ticker" && typeof options?.symbol === "string" && options.symbol.trim()
      ? "ticker"
      : "market";
  const symbol = typeof options?.symbol === "string" ? options.symbol.trim().toUpperCase() : "";

  const [data, setData] = useState<NewsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [pageIdx, setPageIdx] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    async function load() {
      try {
        // limit=20 → 여러 날짜 분량 fetch (날짜별 페이지네이션)
        const qs = scope === "ticker"
          ? `?scope=ticker&symbol=${encodeURIComponent(symbol)}&limit=20`
          : "?scope=market&limit=20";
        const res = await fetch(`/api/ticker/news${qs}`, { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const d = (await res.json()) as NewsResponse;
        if (mountedRef.current) {
          setData(d);
          setLoading(false);
        }
      } catch {
        if (mountedRef.current) setLoading(false);
      }
    }
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(t);
    };
  }, [scope, symbol]);

  // 날짜별 그룹핑 — 최신 날짜가 인덱스 0
  const pages = useMemo(() => {
    const items = data?.items || [];
    if (items.length === 0) return [] as Array<{ dateKey: string; items: NewsItem[] }>;
    const groups = new Map<string, NewsItem[]>();
    for (const it of items) {
      const k = dateKey(it.publishedAt);
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k)!.push(it);
    }
    // unknown 은 맨 앞 (최신 추정), 나머지는 desc
    const knownKeys = Array.from(groups.keys())
      .filter((k) => k !== "unknown")
      .sort((a, b) => (a < b ? 1 : -1));
    const ordered = groups.has("unknown") ? ["unknown", ...knownKeys] : knownKeys;
    return ordered.map((k) => ({ dateKey: k, items: groups.get(k) || [] }));
  }, [data]);

  // 페이지 보정 (data 갱신 시 인덱스 over-bound 방지)
  useEffect(() => {
    if (pageIdx > 0 && pageIdx >= pages.length) setPageIdx(Math.max(0, pages.length - 1));
  }, [pages.length, pageIdx]);

  const currentPage = pages[pageIdx] || null;
  const currentItems = currentPage?.items || [];
  const dateLabel = currentPage ? formatDateLabel(currentPage.dateKey) : "";
  const hasMultiplePages = pages.length > 1;
  const canPrev = pageIdx < pages.length - 1;  // prev = 더 이전 날짜 (인덱스 큼)
  const canNext = pageIdx > 0;                  // next = 더 최근 날짜 (인덱스 작음)

  const title = scope === "ticker" ? `${symbol} 뉴스` : "시장 뉴스";

  return (
    <div className="dw-body dw-news">
      <div className="dw-head">
        <span className="dw-title">{title}</span>
        {hasMultiplePages ? (
          <div className="dw-news-pager" role="group" aria-label="날짜별 페이지">
            <button
              type="button"
              className="dw-news-pager-btn"
              onClick={() => canPrev && setPageIdx((p) => p + 1)}
              disabled={!canPrev}
              aria-label="이전 날짜"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="dw-news-pager-label" aria-live="polite">{dateLabel}</span>
            <button
              type="button"
              className="dw-news-pager-btn"
              onClick={() => canNext && setPageIdx((p) => p - 1)}
              disabled={!canNext}
              aria-label="다음 날짜"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        ) : data?.updatedAt && (
          <span className="dw-news-time">
            {new Date(data.updatedAt).toLocaleTimeString("ko-KR", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        )}
      </div>

      <ul className="dw-news-list">
        {loading && currentItems.length === 0 ? (
          [0, 1, 2].map((i) => (
            <li key={i} className="dw-news-item is-loading">
              <span className="bs-skel dw-news-thumb-skel" />
              <div className="dw-news-body">
                <span
                  className="bs-skel"
                  style={{ width: "90%", height: 13, display: "block", marginBottom: 6 }}
                />
                <span className="bs-skel" style={{ width: "40%", height: 9, display: "block" }} />
              </div>
            </li>
          ))
        ) : currentItems.length > 0 ? (
          currentItems.map((item, i) => (
            <li key={i} className="dw-news-item">
              {item.url ? (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dw-news-link"
                >
                  {item.imageUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={item.imageUrl}
                      alt=""
                      loading="lazy"
                      className="dw-news-thumb"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <span className="dw-news-thumb dw-news-thumb-fallback" aria-hidden="true">
                      📰
                    </span>
                  )}
                  <div className="dw-news-body">
                    <span className="dw-news-title">{item.titleKo || item.title}</span>
                    <span className="dw-news-meta">
                      {item.source && <span className="dw-news-source">{item.source}</span>}
                      {item.publishedAt && (
                        <span className="dw-news-time-rel">
                          {item.source ? " · " : ""}
                          {timeAgoShort(item.publishedAt)}
                        </span>
                      )}
                      <ExternalLink size={9} className="dw-news-ext" />
                    </span>
                  </div>
                </a>
              ) : (
                <div className="dw-news-link">
                  <span className="dw-news-thumb dw-news-thumb-fallback" aria-hidden="true">
                    📰
                  </span>
                  <div className="dw-news-body">
                    <span className="dw-news-title">{item.titleKo || item.title}</span>
                    {item.source && <span className="dw-news-source">{item.source}</span>}
                  </div>
                </div>
              )}
            </li>
          ))
        ) : (
          <li className="dw-news-empty">뉴스 준비중</li>
        )}
      </ul>
    </div>
  );
}

export const marketNewsMeta: WidgetMeta = {
  type: "market-news",
  name: "시장 뉴스",
  description: "시장 전체 혹은 특정 종목 Top 3 뉴스 (AI)",
  icon: "📰",
  category: "info",
  defaultSize: { w: 6, h: 4 },
  availableSizes: [
    { w: 4, h: 4 },
    { w: 6, h: 4 },
    { w: 12, h: 4 },
  ],
  minW: 3,
  maxW: 12,
  minH: 3,
  maxH: 8,
  component: MarketNews,
};
